/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { memo } from 'react';
import { Box, FormHelperText, MenuItem, Select, TextField } from '@mui/material';
import { CustomFieldConditionValue, CustomFieldValueShape, DatePeriodSuffix } from '../typings';

interface Props {
  shape: CustomFieldValueShape;
  suffixes?: DatePeriodSuffix[];
  value: CustomFieldConditionValue;
  onChange: (next: CustomFieldConditionValue) => void;
}

const toDateInputValue = (iso: string | number): string => {
  if (typeof iso !== 'string' || iso.length === 0) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const fromDateInputValue = (raw: string): string => {
  if (!raw) return '';
  const d = new Date(`${raw}T00:00:00.000Z`);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
};

const DateValueInput = ({ shape, suffixes, value, onChange }: Props) => {
  if (shape === 'BETWEEN') {
    const start = value.type === 'BETWEEN' ? value.start : '';
    const end = value.type === 'BETWEEN' ? value.end : '';
    const hasBoth = typeof start === 'string' && start !== '' && typeof end === 'string' && end !== '';
    const isSame = hasBoth && start === end;
    const isOutOfOrder = hasBoth && !isSame && start > end;
    const helperText = isSame
      ? 'Start and end date cannot be the same'
      : isOutOfOrder
      ? 'Start date must be before end date'
      : '';
    return (
      <Box
        css={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <TextField
          fullWidth
          size="small"
          type="date"
          placeholder="Start date"
          value={toDateInputValue(start)}
          onChange={(e) => onChange({ type: 'BETWEEN', start: fromDateInputValue(e.target.value), end })}
          error={Boolean(helperText)}
          InputProps={{ sx: { fontSize: 14 } }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          fullWidth
          size="small"
          type="date"
          placeholder="End date"
          value={toDateInputValue(end)}
          onChange={(e) => onChange({ type: 'BETWEEN', start, end: fromDateInputValue(e.target.value) })}
          error={Boolean(helperText)}
          InputProps={{ sx: { fontSize: 14 } }}
          InputLabelProps={{ shrink: true }}
        />
        {helperText && (
          <FormHelperText
            error
            css={css`
              margin: 0;
              font-size: 12px;
            `}
          >
            {helperText}
          </FormHelperText>
        )}
      </Box>
    );
  }
  if (shape === 'DATE_PERIOD') {
    const period = value.type === 'DATE_PERIOD' ? value : null;
    const suffixOptions: DatePeriodSuffix[] = suffixes && suffixes.length > 0 ? suffixes : ['days'];
    const activeSuffix = period?.suffix ?? suffixOptions[0];
    const periodValue = period?.value ? period.value : '';
    return (
      <Box
        css={css`
          display: flex;
          gap: 8px;
        `}
      >
        <TextField
          fullWidth
          size="small"
          type="number"
          placeholder="0"
          value={periodValue}
          onChange={(e) =>
            onChange({
              type: 'DATE_PERIOD',
              value: e.target.value === '' ? 0 : Number(e.target.value),
              suffix: activeSuffix,
            })
          }
          InputProps={{ sx: { fontSize: 14 } }}
        />
        <Select
          fullWidth
          size="small"
          value={activeSuffix}
          onChange={(e) =>
            onChange({
              type: 'DATE_PERIOD',
              value: typeof periodValue === 'number' ? periodValue : 0,
              suffix: e.target.value as DatePeriodSuffix,
            })
          }
          sx={{ fontSize: 14 }}
        >
          {suffixOptions.map((s) => (
            <MenuItem key={s} value={s} sx={{ fontSize: 14 }}>
              {s}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  }
  const single = value.type === 'VALUE' && typeof value.value === 'string' ? value.value : '';
  return (
    <TextField
      fullWidth
      size="small"
      type="date"
      value={toDateInputValue(single)}
      onChange={(e) => onChange({ type: 'VALUE', value: fromDateInputValue(e.target.value) })}
      InputProps={{ sx: { fontSize: 14 } }}
      InputLabelProps={{ shrink: true }}
    />
  );
};

export default memo(DateValueInput);
